<template>
	<div class="view-wrapper">
		<Spinner v-if="loadingStatus" text="Retrieving data..." classes="text-dark-8 mt-5" spinner-color="var(--c-dark-8)"
			spinner-size="15px" />
		<div v-else>
			<div class="flex flex-wrap justify-between items-center gap-2 -mt-2">
				<TabGroup type="segmented">
					<TabItem :is-active="activeTab === 'cloud'" @click="activeTab = 'cloud'">
						Cloud recording
					</TabItem>
					<Tooltip :active="permanentDvrArchivesPaginated.length === 0"
						content="You don't have any saved recordings yet. Once recordings are saved, they will appear here.">
						<TabItem :is-active="activeTab === 'saved'"
							:disabled="permanentDvrArchivesPaginated.length === 0"
							@click="activeTab = 'saved'">
							Saved recordings
						</TabItem>
					</Tooltip>
				</TabGroup>
				<DropDownMenu v-if="exportingDvrArchivesPaginated.length > 0" type="flat" position="mobile-left" listWidth="w-80">
					<template #toggle-button>
						<span class="text-yellow flex items-center gap-x-2">
							{{ exportingLabelText }}
							<icon-base class="animate-spin">
								<icon-loader />
							</icon-base>
						</span>
					</template>
					<template #menu-items>
						<DropDownMenuItem isText customClasses="!p-2">
							<h5 class="text-dark-9 text-sm font-medium !mb-1">Export in progress</h5>
							<p class="text-dark-8 !text-xs leading-4">
								Once the export is complete, the VOD will be automatically saved in the Video Hosting section.
							</p>
						</DropDownMenuItem>
						<DropDownMenuItem isText customClasses="!p-2 grid grid-cols-[1fr_auto] gap-x-2"
							v-for="video in this.exportingDvrArchivesPaginated" :key="`${video.from}-${video.duration}`">
							<p class="text-white text-tiny truncate">
								{{ video.name }}
							</p>
							<p class="flex justify-end items-center gap-1 row-span-2 text-yellow text-tiny">
								Exporting 
								<icon-base class="animate-spin">
									<icon-loader />
								</icon-base>
							</p>
							<p class="text-dark-6 text-tiny flex items-center gap-1">
								{{ humanTime(video.duration) }}
							</p>
						</DropDownMenuItem>
					</template>
				</DropDownMenu>
			</div>
			<div v-if="activeTab === 'cloud'" class="video-list !pt-4">
				<div class="flex items-center justify-between gap-3 mb-3">
					<div class="min-w-0">
						<h3 class="text-sm text-white font-medium leading-tight">Cloud Recording</h3>
						<p class="text-xs text-surface-7 mt-0.5">
							{{ streamDvrEnabled ? 'Streams are recorded and kept for 3 days — convert to VOD or download anytime.' : 'Auto-recording is off. Your streams won’t be recorded.' }}
						</p>
					</div>
					<Tooltip class="shrink-0" v-if="(this.mediaPulse && this.mediaPulse.alive) && streamDvrEnabled"
						content="You cannot toggle this while stream is live.">
						<FormSwitch labelStyles="block text-sm text-surface-9" :modelValue="this.mediaPulse && this.mediaPulse.alive" disabled />
					</Tooltip>
					<FormSwitch v-else class="shrink-0" label-position="left" label-styles="text-sm text-surface-9"
						:disabled="(this.mediaPulse && this.mediaPulse.alive) && streamDvrEnabled" v-model="localDvrEnabled" />
				</div>
				<Alert type="surface-2" class="!mb-4 !py-2 w-full text-xs">
					<span>
						To ensure stable recording, follow our <a target="_blank" class="underline hover:text-white"
							href="https://docs.castr.com/en/articles/4825093-recommended-settings-for-encoders">recommended encoder settings</a>. Saved files are kept for 3 days in <a class="underline hover:text-white" href="/app/vods">Video Hosting</a>.
					</span>
				</Alert>
				<div v-if="tempDvrArchivesPaginated.length > 0" class="bg-surface-1 rounded-xl p-2">
					<header class="text-xs md:text-tiny uppercase tracking-wide text-surface-7 font-semibold flex flex-wrap py-2 px-1.5 md:px-4">
						<h4 class="w-4/12">Recorded Clips</h4>
						<h4 class="w-3/12 md:w-2/12">Duration</h4>
						<h4 class="w-3/12 md:w-2/12">Recorded at</h4>
					</header>
					<div v-for="video in tempDvrArchivesPaginated" :key="`${video.from}-${video.duration}`"
						class="bg-surface-2 rounded-xl px-1.5 md:px-4 mb-2 last:mb-0">
						<dvr-video-list-item :stream="stream" :video="video" :vod-storage-exceeded="isVodStorageExceeded"
							:is-stream-live="!!(mediaPulse && mediaPulse.alive)"
							class="video-item_tempo" @request-dvr-archive-delete="requestDvrArchiveDelete"
							@reqeust-video-preview="requestVideoPreview" @request-dvr-archive-export="exportDvrArchive" />
					</div>
				</div>
				<FormPagination v-if="tempDvrArchives?.length > 10"
					v-model="tempDvrPageIndex" :total-rows="tempDvrArchives.length" :per-page="10" aria-controls="my-table"
					class="mt-3"></FormPagination>
			</div>
			<div v-if="activeTab === 'saved' && permanentDvrArchivesPaginated.length > 0" class="video-list !pt-4">
				<div class="flex justify-between items-center w-full flex-nowrap md:flex-wrap !mb-3">
					<h3 class="text-sm text-surface-9 font-medium leading-8">
						VOD (Video On Demand)
					</h3>
				</div>
				<p class="text-xs text-surface-8 mb-3">
					Access all files in <a class="underline hover:text-white" href="/app/vods">Content Library</a> for complete
					settings.
					Deleting recorded clips here will also remove them from Content Library
				</p>
				<div class="bg-surface-1 rounded-xl p-2">
					<header class="text-xs md:text-tiny uppercase tracking-wide text-surface-7 font-semibold flex flex-wrap py-2 px-1.5 md:px-4">
						<h4 class="w-4/12">Video</h4>
						<h4 class="w-3/12 md:w-2/12">Duration</h4>
						<h4 class="w-3/12 md:w-2/12">Recorded at</h4>
					</header>
					<div v-for="video in permanentDvrArchivesPaginated" :key="`${video.from}-${video.duration}`"
						class="bg-surface-2 rounded-xl px-1.5 md:px-4 mb-2 last:mb-0">
						<dvr-video-list-item :stream="stream" :video="video" :vod-storage-exceeded="isVodStorageExceeded"
							@request-dvr-archive-delete="requestDvrArchiveDelete" />
					</div>
				</div>
				<FormPagination v-if="permanentDvrArchivesPaginated.length && permanentDvrArchives.length > 10"
					v-model="permanentDvrPaginationIndex" :total-rows="permanentDvrArchives.length" :per-page="10"
					aria-controls="my-table" class="mt-3"></FormPagination>
			</div>
		</div>

		<confirm-modal modal-id="modal-confirm-2" modal-type="danger" message="Do you want to delete this recorded file?"
			@modal-confirm="deleteDvrArchive" />

		<confirm-modal modal-id="modal-confirm-3"
			message="Are you sure you want to delete this file? You won't be able to download it anymore"
			@modal-confirm="deleteDvrArchive" />

		<alert-modal
			:message="exceedStorageErrorMsg || 'You are exceeding your VOD storage limit. Please upgrade to continue'"
			modal-id="alert-dvr-storage-exceeding" ok-text="Ok" />

		<dvr-archive-video-preview-modal v-if="selectedVideo" :stream="stream" :video="selectedVideo"
			:action="selectedVideoAction" :vod-storage-exceeded="isVodStorageExceeded"
			@request-dvr-archive-export-custom-range="exportDvrArchive" />
	</div>
</template>

<script>
import { showModal, hideModal } from '@/Utils/modalBus'
import _ from 'lodash';
import { track, EVENTS } from '@/lib/tracking'
import moment from 'moment';
import prettyMilliseconds from 'pretty-ms';
import Spinner from "@/components/ui/Spinner.vue";
import StreamService from '@/services/StreamService';
import MetricsService from '@/services/MetricsService';
import UserService from '@/services/UserService';
import VodVideoModal from '@/components/modals/VODVideoModal.vue';
import DvrArchiveVideoPreviewModal from '@/components/modals/DvrArchiveVideoPreviewModal.vue';
import AlertModal from '@/components/modals/AlertModal.vue';
import { mapGetters } from 'vuex';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import DvrVideoListItem from '@/components/ui/DVRVideoListItem.vue';
import { timeAgo, bytes } from '@/filtersNew'
import FormSwitch from '../../components/Atoms/FormSwitch.vue';
import TabGroup from '../../components/Atoms/TabGroup.vue';
import TabItem from '../../components/Atoms/TabItem.vue';
import IconInfoSmall from '@/components/icon/IconInfoSmall.vue';
import IconBase from '@/components/icon/IconBase.vue'
import IconLoader from '@/components/icon/IconLoader.vue';
import Tooltip from '@/components/Atoms/Tooltip.vue';
import Alert from '../../components/Atoms/Alert.vue';
import DropDownMenu from '../../components/Atoms/DropDownMenu.vue';
import DropDownMenuItem from '../../components/Atoms/DropDownMenuItem.vue';
import FormPagination from '@/components/Atoms/FormPagination.vue';

export default {
	name: 'StreamsManageClipEpisodes',
	components: {
		Spinner,
		VodVideoModal,
		DvrArchiveVideoPreviewModal,
		ConfirmModal,
		DvrVideoListItem,
		AlertModal,
		FormSwitch,
		IconBase,
		IconInfoSmall,
		Tooltip,
		Alert,
		DropDownMenu,
		DropDownMenuItem,
		IconLoader,
		FormPagination,
		TabGroup,
		TabItem,
	},
	props: {
		stream: {
			type: Object,
			required: true,
			validator(v) {
				return Object.prototype.hasOwnProperty.call(v, '_id');
			},
		},
		mediaPulse: {
			type: Object,
			default() { return {}; }
		},
		streamDvrEnabled: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			processing: true,
			vodEpisodes: [],

			exportingDvrArchives: [],
			permanentDvrArchives: [],
			tempDvrArchives: [],

			selectedVideo: null,
			selectedVideoAction: 'video',
			deletedVideo: null,

			mediaPulseTicks: 0,
			mediaPulseAliveTicks: 0,
			hasSeenNewFeature: false,

			usedVodStorage: 0,
			activeTab: 'cloud',
			exceedStorageErrorMsg: null,
			storagePullTimer: null,
			permanentDvrPaginationIndex: 1,
			tempDvrPageIndex: 1,
			exportingDvrAPageIndex: 1,
		};
	},
	computed: {
		...mapGetters({
			loadingStatus: "Ui/loadingStatus",
			vodStreams: "Streams/vodStreams"
		}),
		isLive() {
			return _.find(this.tempDvrArchives, { live: true });
		},
		vodStorageLimitByte() {
			return parseFloat(this.$store.state.User.maxVodStorage) * (1000 ** 3)
		},
		isVodStorageExceeded() {
			const limit = this.vodStorageLimitByte;
			if (!Number.isFinite(limit)) {
				return false;
			}
			return this.usedVodStorage >= limit;
		},
		permanentDvrArchivesPaginated() {
			return this.permanentDvrArchives.slice((this.permanentDvrPaginationIndex - 1) * 10, (this.permanentDvrPaginationIndex - 1) * 10 + 10)
		},
		tempDvrArchivesPaginated() {
			return this.tempDvrArchives.slice((this.tempDvrPageIndex - 1) * 10, (this.tempDvrPageIndex - 1) * 10 + 10)
		},

		exportingDvrArchivesPaginated() {
			return this.exportingDvrArchives.slice((this.exportingDvrAPageIndex - 1) * 10, (this.exportingDvrAPageIndex - 1) * 10 + 10)
		},
		localDvrEnabled: {
			get() {
				return this.streamDvrEnabled
			},
			async set(value) {
				try {
					await StreamService.saveStreamMetadata(this.stream._id, 'dvrArchive', value);
					this.$emit('dvr-recording-toggle', value);
					this.$notify({ group: 'success', text: 'Setting saved' })
					track(EVENTS.RECORDING_TOGGLED, {
						streamId: this.stream._id,
						streamType: this.stream.type,
						recordingEnabled: value,
					})
				} catch (e) {
					console.error('localDvrEnabled set', e);
					this.$notify({ group: 'error', text: 'Stream DVR toggle failed' });
				}
			}
		},
		exportingLabelText() {
			if (this.exportingDvrArchivesPaginated.length === 1) {
				return 'Exporting 1 file';
			}
			return `Exporting ${this.exportingDvrArchivesPaginated.length} files`
		}
	},
	watch: {
		async mediaPulse() {
			// Cloud recording off → no new archives are produced, so skip the
			// pulse-driven re-fetches of the DVR archive list entirely.
			if (!this.streamDvrEnabled) return;
			this.mediaPulseTicks = this.mediaPulseTicks + 1;
			if (this.mediaPulse && this.mediaPulse.alive) {
				this.mediaPulseAliveTicks = this.mediaPulseAliveTicks + 1;
				if (!this.isLive && this.mediaPulseAliveTicks > 2) {
					await this.setup();
					this.mediaPulseTicks = 0;
					this.mediaPulseAliveTicks = 0;
					if (this.tempDvrArchives[0]) {
						this.tempDvrArchives[0].live = true;
						this.tempDvrArchives[0].blocking = false;
					}
				}

				const len = this.tempDvrArchives.length
				if (len > 1) {
					this.sortTempDvrArchives();
				}
			}

			const description = this.mediaPulse.description || ''
			if (description.indexOf('stream not foun') > -1) {
				if (this.mediaPulseTicks > 0 && this.tempDvrArchives[0]) {
					this.tempDvrArchives[0].blocking = false;
				}

				if (this.isLive) {
					this.mediaPulseTicks = 0;
					this.mediaPulseAliveTicks = 0;
					await this.setup();
				}
			}
		}
	},
	async mounted() {

		this.$store.dispatch('Ui/setLoadingStatus', true);
		this.hasSeenNewFeature = (localStorage.getItem('clips-feature', null, Number) === 1);

		await this.setup();
		await this.initVodStorageUsage();

		this.storagePullTimer = setInterval(() => {
			this.initVodStorageUsage();
		}, 5000);

		_.forEach(this.exportingDvrArchives, (video) => {
			this.startStatusTimer(video);
		});
	},
	methods: {
		humanTime(t) {
      return prettyMilliseconds(t * 1000, { unitCount: 2 });
    },
		async initVodStorageUsage() {
			let bytes = 0;
			try {
				// Prefer using subscription storage (same source as exportDvrArchive)
				const subscriptions = this.$store.state.User.subscriptions || [];
				const activeAIOSub = subscriptions.find((sub) => sub.category === 'live' && sub.enabled === true);

				if (activeAIOSub) {
					const userId = await UserService.getUserId(this.$store.state.User.activatedTeam);
					const storageRes = await MetricsService.getSubscriptionStorage(userId, activeAIOSub.package._id);
					if (storageRes && typeof storageRes.bytes === 'number') {
						bytes = storageRes.bytes;
					}
				}

				// Fallback to local aggregation if subscription storage is not available
				if (!bytes) {
					const streams = this.vodStreams;
					streams.forEach((item) => {
						// count only vod streams without pseudo vod streams
						if (item.type === 'vod' && !item.liveParent) {
							bytes += item.totalBytes;
						}
					});
				}
			} catch (e) {
				console.error('initVodStorageUsage', e);
			}
			this.usedVodStorage = bytes;
		},
		async setup() {
			// this.$store.dispatch('Ui/setLoadingStatus', true);
			// this.processing = true;

			try {
				this.vodEpisodes = await StreamService.getStreamDvrArchives(
					this.stream._id
				);
			} catch (e) {
				this.$notify({
					group: 'error',
					text: 'could not retrieve saved vod(s)'
				});
			}
			this.vodEpisodes = _.map(this.vodEpisodes, vodEpisode => {
				const ep = _.assign({}, vodEpisode, {
					startTimeDate: new Date(vodEpisode.startTime),
					endTimeDate: new Date(vodEpisode.endTime)
				});

				ep.fileName = `${timeAgo(
					ep.startTimeDate
				)}`;
				ep.fileName = ep.fileName.replace('a few seconds ago', 'a seconds ago');
				ep.creationTime = ep.startTimeDate;

				// ep.embedUrl = this.getEpisodeEmbedUrl(ep);
				// ep.iframeSnippet = this.getEpisodeIframeSnippet(ep);

				ep.live = false;
				ep.expanded = false;
				ep.streamType = this.stream.type;
				ep.blocking = false;
				ep.sizeRaw = ep.size
				ep.size = ep.size ? bytes(ep.size, false, 2) : '--';

				ep.exportProcessing = false;
				ep.exportStatusTimer = null;

				return ep;
			});

			this.permanentDvrArchives = _.filter(this.vodEpisodes, { exportStatus: 'EXPORTED' }) || []
			this.tempDvrArchives = _.filter(this.vodEpisodes, (el) => {
				return el.exportStatus === 'NOT_EXPORTED' && (Math.round(new Date().getTime() / 1000) - el.from) < 86400 * 4;
			}) || []
			this.exportingDvrArchives = _.filter(this.vodEpisodes, (i) => { return i.exportStatus === 'EXPORTING' || i.exportStatus === 'FAILED' }) || []

			this.sortTempDvrArchives();
			this.sortPermanentDvrArchives();

			if (this.tempDvrArchives.length > 0 && this.stream.enabled) {
				console.log('blocking= true 1st elem')
				this.tempDvrArchives[0].blocking = true;
			}
			// this.processing = false;
			this.$store.dispatch('Ui/setLoadingStatus', false);
		},
		async exportDvrArchive(video) {
			let currentUsage = this.usedVodStorage;

			// Use subscription storage (which includes DVR usage) for quota checks when available
			try {
				const subscriptions = this.$store.state.User.subscriptions || [];
				const activeAIOSub = subscriptions.find((sub) => sub.category === 'live' && sub.enabled === true);

				if (activeAIOSub) {
					const userId = await UserService.getUserId(this.$store.state.User.activatedTeam);
					const storageRes = await MetricsService.getSubscriptionStorage(userId, activeAIOSub.package._id);
					if (storageRes && typeof storageRes.bytes === 'number') {
						currentUsage = storageRes.bytes;
					}
				}
			} catch (e) {
				console.error('exportDvrArchive: failed to load subscription storage, falling back to client usage', e);
			}

			// keep local UI usage in sync with latest server value
			this.usedVodStorage = currentUsage;

			if (currentUsage >= this.vodStorageLimitByte) {
				this.exceedStorageErrorMsg = `Your VOD storage capacity cannot exceed ${(this.vodStorageLimitByte / 1000 ** 3)}GB. Upgrade your plan to continue`;
				showModal('alert-dvr-storage-exceeding');
				return;
			}

			const again = video.again;
			video.exportProcessing = true;

			let name = `${moment(video.creationTime).format('DD/MM/YYYY')}_full`

			try {
				const archiveObj = {
					streamId: this.stream._id, from: video.from,
					duration: video.duration, server: video.server,
					abr: video.abr, namedHls: video.namedHls,
					packingCompat: true
				};

				if (Object.hasOwn(video, 'packingCompat')) {
					archiveObj.packingCompat = video.packingCompat
				}

				if (video.customRange) {
					archiveObj.from = video.customRange.from;
					archiveObj.duration = video.customRange.duration;

					const dur = prettyMilliseconds(video.customRange.duration * 1000).replace(' ', '_');
					name = `${moment(video.creationTime).format('DD/MM/YYYY')}_range_${dur}`
				}

				if (video.filterTrack) {
					archiveObj.filterTrack = video.filterTrack
				}

				archiveObj.name = name;
				await StreamService.exportStreamDvrArchive(archiveObj);
				track(EVENTS.LIVE_TO_VOD_EXPORT_STARTED, {
					streamId: this.stream._id,
					streamType: this.stream.type,
					exportType: video.customRange ? 'range' : 'full',
				});
			} catch (err) {
				console.log('exportDvrArchive', err);
				if (!err.toString().includes('timeout of')) {
					const errText = _.get(err, 'response.data.message')
					this.$notify({
						group: 'error',
						text: errText || 'Couldn\'t start exporting process. try again later'
					});
					video.exportProcessing = false;
					return
				}
			}

			let newVideo = video
			if (!again) {
				newVideo = _.cloneDeep(video);
				newVideo.name = name;
				if (video.customRange) {
					newVideo.from = video.customRange.from;
					newVideo.duration = video.customRange.duration;
					delete video.customRange;
				}
				this.moveExporting(newVideo)
			}

			newVideo.exportStatus = 'EXPORTING';
			this.startStatusTimer(newVideo);
			video.exportProcessing = false;
		},
		startStatusTimer(video) {
			video.exportStatusTimer = setInterval(async () => {
				const q = {
					streamId: this.stream._id, from: video.from,
					duration: video.duration, abr: video.abr, namedHls: video.namedHls
				};
				if (video.customRange) {
					q.from = video.customRange.from;
					q.duration = video.customRange.duration;
				}
				try {
					const resp = await StreamService.getStreamDvrExportStatus(q);
					if (resp.success) {
						clearInterval(video.exportStatusTimer);

						video.exportStatus = 'EXPORTED';
						video.sizeRaw = resp.size
						video.size = bytes(resp.size, false, 2)

						this.moveExported(video)
						track(EVENTS.LIVE_TO_VOD_CREATED, {
							streamId: this.stream._id,
							streamType: this.stream.type,
						})
						return
					}

					if (!resp.success && resp.reason === 'not found') {
						console.log('not found');
					}

					if (!resp.success && resp.reason === 'export failed') {
						video.exportStatus = 'FAILED';
						clearInterval(video.exportStatusTimer);
					}
				} catch (err) {
					console.log('getStreamDvrExportStatus', err);
				}
			}, 3000)
		},
		requestDvrArchiveDelete(video) {
			this.deletedVideo = video;
			if (video.exportStatus === 'NOT_EXPORTED') {
				showModal('modal-confirm-3');
				return
			}

			showModal('modal-confirm-2');
			return
		},
		async deleteDvrArchive() {
			const video = this.deletedVideo;

			if (video.exportStatus === 'NOT_EXPORTED') {
				this.deleteTempDvrArchive(video);
				return
			}

			const q = {
				streamId: this.stream._id,
				from: video.from,
				duration: video.duration,
				abr: video.abr,
				namedHls: video.namedHls
			};
			try {
				await StreamService.deleteStreamDvrArchive(q);
				_.remove(this.permanentDvrArchives, (item) => {
					return item.duration === video.duration && item.from === video.from
				});
				_.remove(this.exportingDvrArchives, (item) => {
					return item.duration === video.duration && item.from === video.from
				});
				this.sortPermanentDvrArchives();

				console.log(`delete archive range=${video.from}-${video.duration}`, video);
			} catch (err) {
				this.$notify({
					group: 'error',
					text: 'Couldn\'t process delete request. try again later'
				});
			}
		},
		async deleteTempDvrArchive(video) {
			console.log('deleteTempDvrArchive', video)
			const q = {
				streamId: this.stream._id,
				from: video.from,
				duration: video.duration,
				abr: video.abr,
				storageServer: video.server,
				namedHls: video.namedHls
			};
			try {
				await StreamService.deleteStreamTempDvrArchive(q);
				_.remove(this.tempDvrArchives, (item) => {
					return item.duration === video.duration && item.from === video.from
				});
				this.sortTempDvrArchives();
				console.log(`delete tmp dvr archive range=${video.from}-${video.duration}`, video);
			} catch (err) {
				this.$notify({
					group: 'error',
					text: 'Couldn\'t process delete request. try again later'
				});
			}
		},
		sortPermanentDvrArchives() {
			this.permanentDvrArchives.sort(function compare(a, b) {
				var dateA = new Date(a.from);
				var dateB = new Date(b.from);
				return dateB - dateA;
			});
		},
		sortTempDvrArchives() {
			this.tempDvrArchives.sort(function compare(a, b) {
				var dateA = new Date(a.from);
				var dateB = new Date(b.from);
				return dateB - dateA;
			});
		},
		async requestVideoPreview(payload) {
			if (payload && payload.video) {
				this.selectedVideo = payload.video;
				this.selectedVideoAction = payload.action || 'video';
			} else {
				this.selectedVideo = payload;
				this.selectedVideoAction = 'video';
			}
			setTimeout(() => {
				showModal('modal-dvr-archive-video-preview');
			}, 100);
		},
		markSeenNewFeature() {
			localStorage.setItem('clips-feature', 1);
			this.hasSeenNewFeature = true;
		},
		clipboardCopy(text) {
			try {
				if (text instanceof Function) text = text();

				this.$copyText(text);
				this.$notify({ group: 'info', text: 'Copied to clipboard' });
			} catch (e) { }
		},
		async moveExporting(video) {
			this.exportingDvrArchives.push(video)
			return
		},
		async moveExported(video) {
			_.remove(this.exportingDvrArchives, (item) =>
				item.duration === video.duration && item.from === video.from
			);
			this.permanentDvrArchives.push(video);
			this.sortPermanentDvrArchives();
			this.$notify({ group: 'success', text: 'Saved as VOD successfully. Check your video in the VOD (Video on Demand) tab.' });
			return
		},
		beforeUnmount() {
			clearInterval(this.storagePullTimer);
			_.forEach(this.vodEpisodes, (video) => {
				console.log(`beforeUnmount range=${video.from}-${video.duration}`, this.video);
				clearInterval(video.exportStatusTimer);
			});
		}
	}
};
</script>
<style scoped>
:deep(.video-item_tempo .collapse),
:deep(.video-item_tempo .collapsing) {
	display: none !important;
}
</style>
